import React, { useContext, useEffect, useId, useRef, useState } from "react";
import {
  FiHeart,
  FiChevronDown,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { formatPrice, siteConfig } from "../Datas/productData";
import "../css/header.scss";
import { CartContext } from "./CartContext";

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchId = useId();
  const {
    cartItems = [],
    wishlist = [],
    cartCount: storedCartCount,
    wishlistCount: storedWishlistCount,
  } = useContext(CartContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const previousLocationRef = useRef({
    hash: location.hash,
    pathname: location.pathname,
    search: location.search,
  });

  const cartCount =
    storedCartCount ??
    cartItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
  const wishlistCount = storedWishlistCount ?? wishlist.length;

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsMoreOpen(false);
    const pageTitles = {
      "/cartpage": `Shopping bag | ${siteConfig.displayName}`,
      "/checkout": `Checkout | ${siteConfig.displayName}`,
      "/paysuccess": `Order received | ${siteConfig.displayName}`,
      "/about": `About us | ${siteConfig.displayName} ${siteConfig.brandLine}`,
      "/contact": `Contact | ${siteConfig.displayName}`,
      "/shipping": `Shipping & delivery | ${siteConfig.displayName}`,
      "/returns": `Returns | ${siteConfig.displayName}`,
      "/privacy": `Privacy policy | ${siteConfig.displayName}`,
      "/terms": `Terms & conditions | ${siteConfig.displayName}`,
      "/faqs": `FAQs | ${siteConfig.displayName}`,
      "/cookies": `Cookie policy | ${siteConfig.displayName}`,
    };
    if (pageTitles[location.pathname]) {
      document.title = pageTitles[location.pathname];
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const previousLocation = previousLocationRef.current;
    previousLocationRef.current = {
      hash: location.hash,
      pathname: location.pathname,
      search: location.search,
    };

    const withoutPage = (searchValue) => {
      const params = new URLSearchParams(searchValue);
      params.delete("page");
      params.sort();
      return params.toString();
    };
    const isPaginationNavigation =
      previousLocation.pathname === location.pathname &&
      previousLocation.hash === location.hash &&
      previousLocation.search !== location.search &&
      withoutPage(previousLocation.search) === withoutPage(location.search);

    if (isPaginationNavigation) return;

    const rawHash = location.hash.slice(1);
    let targetId = rawHash;

    try {
      targetId = decodeURIComponent(rawHash);
    } catch {
      targetId = rawHash;
    }

    const hashTarget = targetId ? document.getElementById(targetId) : null;
    if (hashTarget && typeof hashTarget.scrollIntoView === "function") {
      hashTarget.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.hash, location.key, location.pathname, location.search]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        setIsSearchOpen(false);
        setIsMoreOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMenuOpen]);

  const handleSearch = (event) => {
    event.preventDefault();
    const value = searchTerm.trim();
    navigate(value ? `/shop?search=${encodeURIComponent(value)}` : "/shop");
  };

  const isCurrentLink = (target) => {
    const [pathname, query = ""] = target.split("?");
    if (location.pathname !== pathname) return false;
    return query ? location.search.includes(query) : !location.search;
  };

  return (
    <header className="lk-header">
      <div className="lk-header__announcement">
        <span className="lk-header__announcement-detail">
          International · EN
        </span>

        <span className="lk-header__announcement-detail">
          Free delivery from {formatPrice(siteConfig.shipping.freeThreshold)}
        </span>
      </div>

      <div className="lk-header__main">
        <Link
          className="lk-header__wordmark"
          to="/"
          aria-label={`${siteConfig.displayName} home`}
        >
          <svg
            className="lk-header__brand-symbol"
            viewBox="0 0 64 40"
            aria-hidden="true"
          >
            <path d="M4 34 17 6h7l13 28h-8l-2.5-6H14.2L12 34H4Zm13-13h6.8L20.3 12 17 21Z" />
            <path d="M34 34V6h7l7.5 11L56 6h4v28h-7V18l-4.5 6.5L41 15v19h-7Z" />
          </svg>
          <span>{siteConfig.logo.text}</span>
          <small>{siteConfig.logo.subline}</small>
        </Link>

        <nav className="lk-header__desktop-nav" aria-label="Primary navigation">
          {siteConfig.navigation.map((item) => (
            <Link
              key={`${item.label}-${item.to}`}
              to={item.to}
              className={`${item.accent ? "is-accent" : ""} ${
                isCurrentLink(item.to) ? "is-current" : ""
              }`}
              aria-current={isCurrentLink(item.to) ? "page" : undefined}
            >
              {item.label}
            </Link>
          ))}
          <div className="lk-header__more">
            <button
              type="button"
              aria-expanded={isMoreOpen}
              aria-controls="more-navigation"
              onClick={() => setIsMoreOpen((open) => !open)}
            >
              More <FiChevronDown aria-hidden="true" />
            </button>
            {isMoreOpen && (
              <div id="more-navigation" className="lk-header__more-menu">
                <Link to="/contact">Contact Us</Link>
                <Link to="/privacy">Privacy policy</Link>
                <Link to="/cookies">Cookie policy</Link>
                <Link to="/terms">Terms &amp; conditions</Link>
                <Link to="/faqs">FAQs</Link>
              </div>
            )}
          </div>
        </nav>

        <div className="lk-header__actions" aria-label="Store tools">
          <button
            type="button"
            className="lk-header__icon-button lk-header__search-trigger"
            aria-label="Search"
            aria-expanded={isSearchOpen}
            aria-controls="site-search"
            onClick={() => setIsSearchOpen((open) => !open)}
          >
            {isSearchOpen ? (
              <FiX aria-hidden="true" />
            ) : (
              <FiSearch aria-hidden="true" />
            )}
            <span>Search</span>
          </button>
          <Link
            className="lk-header__icon-button"
            to="/shop?view=wishlist"
            aria-label={`Wishlist, ${wishlistCount} items`}
          >
            <FiHeart aria-hidden="true" />
            <span>Wishlist</span>
            <strong>{wishlistCount}</strong>
          </Link>
          <Link
            className="lk-header__icon-button"
            to="/cartpage"
            aria-label={`Shopping bag, ${cartCount} items`}
          >
            <FiShoppingBag aria-hidden="true" />
            <span>Bag</span>
            <strong>{cartCount}</strong>
          </Link>
        </div>
      </div>

      {isSearchOpen && (
        <div className="lk-header__search-panel" id="site-search">
          <form role="search" onSubmit={handleSearch}>
            <label htmlFor={searchId}>What are you looking for?</label>
            <div>
              <FiSearch aria-hidden="true" />
              <input
                id={searchId}
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search bags, purses and accessories"
                autoComplete="off"
                autoFocus
              />
              <button type="submit">Search</button>
            </div>
          </form>
        </div>
      )}

      {isMenuOpen && (
        <>
          <button
            className="lk-header__drawer-backdrop"
            type="button"
            aria-label="Close navigation menu"
            onClick={() => setIsMenuOpen(false)}
          />
          <aside
            className="lk-header__drawer"
            id="mobile-navigation"
            aria-label="Mobile navigation"
          >
            <div className="lk-header__drawer-head">
              <span className="lk-header__drawer-mark">
                {siteConfig.displayName}
              </span>
              <button
                type="button"
                aria-label="Close navigation menu"
                onClick={() => setIsMenuOpen(false)}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
            <nav aria-label="Mobile primary navigation">
              {siteConfig.navigation.map((item) => (
                <Link
                  key={`mobile-${item.label}-${item.to}`}
                  to={item.to}
                  className={item.accent ? "is-accent" : undefined}
                >
                  {item.label}
                  <span aria-hidden="true">→</span>
                </Link>
              ))}
            </nav>
            <div className="lk-header__drawer-links">
              <Link to="/shop?view=wishlist">
                <FiHeart aria-hidden="true" /> Wishlist ({wishlistCount})
              </Link>
              <Link to="/cartpage">
                <FiShoppingBag aria-hidden="true" /> Shopping bag ({cartCount})
              </Link>
              <Link to="/contact">Customer care</Link>
              <Link to="/privacy">Privacy policy</Link>
              <Link to="/cookies">Cookie policy</Link>
              <Link to="/terms">Terms &amp; conditions</Link>
              <Link to="/faqs">FAQs</Link>
            </div>
            <p>{siteConfig.description}</p>
          </aside>
        </>
      )}
    </header>
  );
}

export default Header;
